import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { CardComponentProps } from "@~/types/types";
import { PDPLink } from "../__shared__/PDPLink/PDPLink";
import { useCallback, useEffect, useRef, useState } from "react";
import { getDeviceCapabilities } from "./utils";
import styles from "./ImageList.module.scss";
import { Image } from "../__shared__/Image/Image";

export const ImageList = ({
  index,
  product,
}: Pick<CardComponentProps, "index" | "product">) => {
  const { variant } = useVariant();
  const variantHasAlternativeImages =
    variant?.images?.alternatives && variant.images.alternatives.length > 0;
  const carouselElement = useRef<HTMLAnchorElement | null>(null);
  const [displayAllAlternateImages, setDisplayAllAlternateImages] =
    useState(false);

  const hoverPrimaryImage = useCallback((slide: boolean) => {
    const carouselElementCurrent = carouselElement.current;
    if (!carouselElementCurrent) {
      return;
    }

    const firstImageDisplayed =
      carouselElementCurrent.scrollLeft <= carouselElementCurrent.clientWidth;

    if (firstImageDisplayed) {
      carouselElementCurrent.scrollLeft = slide
        ? carouselElementCurrent.clientWidth
        : 0;
    }
  }, []);

  useEffect(
    function subscribeToHoverPreviewInteractions() {
      const carouselElementCurrent = carouselElement.current;
      if (
        !carouselElementCurrent ||
        !variantHasAlternativeImages ||
        !getDeviceCapabilities().hasHover
      ) {
        return;
      }

      const handleMouseEnter = () => {
        setDisplayAllAlternateImages((currentValue) => currentValue || true);
        hoverPrimaryImage(true);
      };
      const handleMouseLeave = () => hoverPrimaryImage(false);

      carouselElementCurrent.addEventListener("mouseenter", handleMouseEnter);
      carouselElementCurrent.addEventListener("mouseleave", handleMouseLeave);

      return function unsubscribeFromHoverPreviewInteractions() {
        carouselElementCurrent.removeEventListener(
          "mouseenter",
          handleMouseEnter,
        );
        carouselElementCurrent.removeEventListener(
          "mouseleave",
          handleMouseLeave,
        );
      };
    },
    [hoverPrimaryImage, variantHasAlternativeImages],
  );

  useEffect(
    function subscribeToTouchPreviewInteraction() {
      const carouselElementCurrent = carouselElement.current;
      if (
        !carouselElementCurrent ||
        !variantHasAlternativeImages ||
        !getDeviceCapabilities().hasTouch
      ) {
        return;
      }

      const revealAlternateImages = () => setDisplayAllAlternateImages(true);
      carouselElementCurrent.addEventListener(
        "touchstart",
        revealAlternateImages,
        {
          once: true,
        },
      );

      return function unsubscribeFromTouchPreviewInteraction() {
        carouselElementCurrent.removeEventListener(
          "touchstart",
          revealAlternateImages,
        );
      };
    },
    [variantHasAlternativeImages],
  );

  const isFirstCard = index === 0;
  useEffect(
    function previewFirstCardOnTouchDevices() {
      if (isFirstCard && getDeviceCapabilities().hasTouch) {
        carouselElement.current?.classList.add(styles.imageList__preview!);
      }
    },
    [isFirstCard],
  );

  if (!variant?.images.primary) {
    return null;
  }

  const primaryImgUrl = variant?.images.primary.split("?")[0] || "";
  const altImageUrls = variant?.images?.alternatives || [];

  return (
    <PDPLink
      product={product}
      componentName="ImageList"
      className={styles.imageList}
      testId="image-list"
      ref={carouselElement}
      ariaLabel="product image carousel"
    >
      <div className={styles.imageListItem}>
        <Image
          url={primaryImgUrl}
          title={product.title}
          colour={variant?.colour?.colour || ""}
          index={index}
        />
      </div>
      {variantHasAlternativeImages && altImageUrls[0] && (
        <div className={styles.imageListItem} key={altImageUrls[0]}>
          <Image
            url={altImageUrls[0]}
            title={`${product.title} - view 2`}
            colour={variant?.colour?.colour || ""}
            index={index}
          />
        </div>
      )}
      {variantHasAlternativeImages &&
        displayAllAlternateImages &&
        altImageUrls.length > 1 &&
        altImageUrls.slice(1).map((url) => {
          return (
            <div className={styles.imageListItem} key={url}>
              <Image
                url={url}
                title={`${product.title} - view ${index + 3}`}
                colour={variant?.colour?.colour || ""}
                index={index}
              />
            </div>
          );
        })}
    </PDPLink>
  );
};
